import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { SystemDescriptions } from "src/common/descriptions"
import { Service } from "src/common/models/service"
import { RootState } from "src/state/reducer"
import { ServiceError } from "../state/types"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { GET_SERVICE_BY_ID_API, UPSERT_SERVICE_API } from "../state/actionTypes"
import { getServiceByIdApi, getServiceCategoriesApi, upsertServiceApi } from "../state/action"
import { Urls } from "src/common/urls"
import { CancelConfirmationModal } from "src/common/descriptions/components/cancelConfirmationModal"
import { ModalSuccess } from "src/components/ModalSuccess"
import { Loader } from "src/components/Loader"
import { PageCard } from "src/common/styles/styles"
import InnerLoader from "src/components/InnerLoader"
import { ServiceForm } from "../ServiceForm"

interface ReduxProps {
    error?: ServiceError
    isUpsertingService: boolean
    upsertServiceSuccess: boolean
    isGettingService: boolean
    currentService?: Service
}

export const CreateService = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const descriptions = SystemDescriptions.PAGES.SERVICE.CREATE

    const { id: serviceId } = useParams<{ id: string }>();

    const [modalConfirmationOpen, setModalConfirmationOpen] = useState<boolean>(false)
    const [modalCancelOpen, setModalCancelOpen] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<Service | undefined>()
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state?.service?.error,
        isUpsertingService: serviceLoading(state, [UPSERT_SERVICE_API]),
        upsertServiceSuccess: state.service.upsertServiceSuccess,
        isGettingService: serviceLoading(state, [GET_SERVICE_BY_ID_API]),
        currentService: state.service.currentService,
    }))

    useEffect(() => {
        dispatch(getServiceCategoriesApi())

        if (serviceId) {
            dispatch(getServiceByIdApi(Number(serviceId)))
        }
    }, [])

    const onFinish = (service: Partial<Service>) => {
        setFormValues(service as Service)
        setModalConfirmationOpen(true)
    }

    const onCancel = () => {
        setModalCancelOpen(true)
    }

    const handleConfirmationAccept = () => {
        setModalConfirmationOpen(false)
        setActionButtonClicked(true)
        dispatch(upsertServiceApi(formValues as Service))
    }

    const handleCancelAccept = () => {
        navigate(Urls.FRONTEND.SERVICE.SERVICES)
    }

    const renderModals = () => (
        <>
            <CancelConfirmationModal
                isVisible={modalConfirmationOpen}
                onAccept={handleConfirmationAccept}
                onCancel={() => {
                    setModalConfirmationOpen(false)
                }}
                title={descriptions.CONFIRM_MODAL.TITLE}
                okText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
                description={descriptions.CONFIRM_MODAL.DESCRIPTION}
            />
            <CancelConfirmationModal
                isVisible={modalCancelOpen}
                onAccept={handleCancelAccept}
                onCancel={() => { setModalCancelOpen(false) }}
                title={descriptions.CANCEL_MODAL.TITLE}
                okText={descriptions.CANCEL_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CANCEL_MODAL.CANCEL_BUTTON}
                description={descriptions.CANCEL_MODAL.DESCRIPTION}
            />
            <ModalSuccess
                isVisible={(reduxProps.upsertServiceSuccess) && actionButtonClicked}
                title={descriptions.SUCCESS_MODAL.TITLE}
                description={descriptions.SUCCESS_MODAL.DESCRIPTION}
                onPrimaryAction={handleCancelAccept}
                onSecondaryActiont={handleCancelAccept}
                primaryLabel={descriptions.SUCCESS_MODAL.PRIMARY_LABEL}
                secondaryLabel={descriptions.SUCCESS_MODAL.SECONDARY_LABEL}
                hideSecondaryButton={true}
            />
        </>
    )

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isUpsertingService}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    return (
        <>
            <PageCard size="small" title={descriptions.TITLE}>
                {
                    reduxProps.isGettingService
                        ? <InnerLoader />
                        : <ServiceForm
                            onFinish={onFinish}
                            onCancel={onCancel}
                            initialValues={reduxProps.currentService}
                        />
                }
            </PageCard>
            {renderModals()}
            {renderLoader()}
        </>
    )
}
